/**
 * useAnalytics Composable
 * Provides safe analytics event emission for components
 * Task: 112-121-T003
 */

import { inject } from 'vue'
import { useNuxtApp } from 'nuxt/app'
import {
  safeEmitAnalytics,
  getCurrentTimestamp,
  ANALYTICS_CLIENT_KEY,
  type AnalyticsClient,
  type AppInsightsClient,
  type ROIBlockRenderedEvent,
  type SubstantiationLinkClickedEvent,
  type PricingBlockRenderedEvent,
  type PricingCTAClickedEvent,
  type CTAClickedEvent,
  type PageViewEvent,
  type BookingsEmbedStatusEvent,
  type BookingsFallbackTriggeredEvent,
  type BlogSearchSubmittedEvent,
  type BlogSearchNoResultsEvent,
  type BlogSearchResultClickedEvent,
  type BlogListingViewedEvent,
  type BlogArticleViewedEvent,
  type BlogArticleClickedEvent,
  type BlogPaginationClickedEvent,
  type BlogScrollDepthEvent,
  type BlogReadTimeEvent,
  type BlogFilterAppliedEvent,
  type BlogFilterClearedEvent,
  type FormSubmittedEvent,
  type ResourceDownloadedEvent,
  type PurchaseCompletedEvent,
  type BookingCompletedEvent,
  type SchemaValidationPassedEvent,
  type SchemaValidationFailedEvent,
  type MetaValidationPassedEvent,
  type MetaValidationFailedEvent,
} from '../types/analytics'

/**
 * Composable for emitting analytics events with guards
 * Returns safe wrappers for event emission
 * Supports dual tracking: GA4 (gtag plugin) + Azure Application Insights
 *
 * @returns Object with emit functions for ROI block events
 */
export function useAnalytics() {
  const nuxtApp = useNuxtApp()

  // Try to get Application Insights client from plugin
  const appInsightsClient = (nuxtApp.$appInsights as AppInsightsClient | undefined) || null

  // Inject optional custom analytics client (fallback or additional client)
  const customClient = inject<AnalyticsClient | null>(ANALYTICS_CLIENT_KEY, null)

  // Use App Insights if available, otherwise use custom client
  const analyticsClient: AnalyticsClient | AppInsightsClient | null =
    appInsightsClient || customClient

  /**
   * Emit ROI block rendered event
   *
   * @param product - Product name (e.g., "Review", "Verify")
   * @param metricsCount - Number of metrics in block
   * @param hasSubstantiation - Whether substantiation links present
   */
  function emitROIBlockRendered(
    product: string,
    metricsCount: number,
    hasSubstantiation: boolean
  ): void {
    const event: ROIBlockRenderedEvent = {
      event_name: 'roi_block_rendered',
      product,
      metrics_count: metricsCount,
      has_substantiation: hasSubstantiation,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit substantiation link clicked event
   *
   * @param linkTitle - Link display text
   * @param linkUrl - Link target URL
   * @param linkType - Link type classification
   * @param product - Product context
   */
  function emitSubstantiationLinkClicked(
    linkTitle: string,
    linkUrl: string,
    linkType: 'internal' | 'external',
    product: string
  ): void {
    const event: SubstantiationLinkClickedEvent = {
      event_name: 'substantiation_link_clicked',
      link_title: linkTitle,
      link_url: linkUrl,
      link_type: linkType,
      product,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit pricing block rendered event
   *
   * @param product - Product name (e.g., "Review", "Verify")
   * @param takeawaysCount - Number of takeaways displayed
   * @param hasCTA - Whether CTA button is present
   */
  function emitPricingBlockRendered(
    product: string,
    takeawaysCount: number,
    hasCTA: boolean
  ): void {
    const event = createPricingBlockRenderedEvent(product, takeawaysCount, hasCTA)
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit pricing CTA clicked event
   *
   * @param ctaText - Button text label
   * @param ctaUrl - Target URL
   * @param linkType - Internal or external link
   * @param product - Product context
   */
  function emitPricingCTAClicked(
    ctaText: string,
    ctaUrl: string,
    linkType: 'internal' | 'external',
    product: string
  ): void {
    const event = createPricingCTAClickedEvent(ctaText, ctaUrl, linkType, product)
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit CTA clicked event
   *
   * @param location - CTA location (hero, mid, footer)
   * @param product - Product context (review, verify, insight)
   * @param buttonText - Button text
   * @param destination - Destination type (e.g., 'bookings', 'form'); defaults to 'bookings'
   */
  function emitCTAClicked(
    location: string,
    product: string,
    buttonText: string,
    destination: string = 'bookings'
  ): void {
    const event: CTAClickedEvent = {
      event_name: 'cta_clicked',
      location,
      product,
      button_text: buttonText,
      destination,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitPageView(pagePath: string, pageTitle: string): void {
    const event: PageViewEvent = {
      event_name: 'page_view',
      page_path: pagePath,
      page_title: pageTitle,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBookingsEmbedStatus(status: 'loaded' | 'failed' | 'timeout'): void {
    const event: BookingsEmbedStatusEvent = {
      event_name: 'bookings_embed_status',
      status,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBookingsFallbackTriggered(reason: string): void {
    const event: BookingsFallbackTriggeredEvent = {
      event_name: 'bookings_fallback_triggered',
      reason,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogSearchSubmitted(
    searchQuery: string,
    resultCount: number,
    activeFilters: { product?: string; category?: string } | null,
    responseTimeMs: number
  ): void {
    const event: BlogSearchSubmittedEvent = {
      event_name: 'blog_search_submitted',
      search_query: searchQuery,
      result_count: resultCount,
      active_filters: activeFilters,
      response_time_ms: responseTimeMs,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogSearchNoResults(
    searchQuery: string,
    activeFilters: { product?: string; category?: string } | null
  ): void {
    const event: BlogSearchNoResultsEvent = {
      event_name: 'blog_search_no_results',
      search_query: searchQuery,
      active_filters: activeFilters,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogSearchResultClicked(
    searchQuery: string,
    clickedArticleId: string,
    resultPosition: number
  ): void {
    const event: BlogSearchResultClickedEvent = {
      event_name: 'blog_search_result_clicked',
      search_query: searchQuery,
      clicked_article_id: clickedArticleId,
      result_position: resultPosition,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogListingViewed(
    pagePath: string,
    pageTitle: string,
    articleCount: number,
    currentPage: number
  ): void {
    const event: BlogListingViewedEvent = {
      event_name: 'blog_listing_viewed',
      page_path: pagePath,
      page_title: pageTitle,
      article_count: articleCount,
      current_page: currentPage,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogArticleViewed(
    pagePath: string,
    articleSlug: string,
    articleTitle: string,
    categories: string[],
    readingTimeMinutes: number
  ): void {
    const event: BlogArticleViewedEvent = {
      event_name: 'blog_article_viewed',
      page_path: pagePath,
      article_slug: articleSlug,
      article_title: articleTitle,
      categories,
      reading_time_minutes: readingTimeMinutes,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogArticleClicked(
    articlePath: string,
    articleSlug: string,
    articleTitle: string,
    articlePosition: number,
    currentPage: number
  ): void {
    const event: BlogArticleClickedEvent = {
      event_name: 'blog_article_clicked',
      article_path: articlePath,
      article_slug: articleSlug,
      article_title: articleTitle,
      article_position: articlePosition,
      current_page: currentPage,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogPaginationClicked(
    pagePath: string,
    fromPage: number,
    toPage: number,
    totalPages: number
  ): void {
    const event: BlogPaginationClickedEvent = {
      event_name: 'blog_pagination_clicked',
      page_path: pagePath,
      from_page: fromPage,
      to_page: toPage,
      total_pages: totalPages,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogScrollDepth(pagePath: string, pageType: 'listing' | 'article'): void {
    const event: BlogScrollDepthEvent = {
      event_name: 'blog_scroll_depth',
      page_path: pagePath,
      page_type: pageType,
      threshold_percent: 50,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogReadTime(pagePath: string, pageType: 'listing' | 'article'): void {
    const event: BlogReadTimeEvent = {
      event_name: 'blog_read_time',
      page_path: pagePath,
      page_type: pageType,
      threshold_seconds: 90,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogFilterApplied(activeFilters: { product?: string | null; category?: string | null }): void {
    const event: BlogFilterAppliedEvent = {
      event_name: 'blog_filter_applied',
      active_filters: activeFilters,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitBlogFilterCleared(): void {
    const event: BlogFilterClearedEvent = {
      event_name: 'blog_filter_cleared',
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitSchemaValidationPassed(
    pageType: 'home' | 'product' | 'blog',
    schemaType: 'organization' | 'product' | 'article' | 'faq' | 'breadcrumb',
  ): void {
    const event: SchemaValidationPassedEvent = {
      event_name: 'schema_validation_passed',
      page_type: pageType,
      schema_type: schemaType,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitSchemaValidationFailed(
    pageType: 'home' | 'product' | 'blog',
    schemaType: 'organization' | 'product' | 'article' | 'faq' | 'breadcrumb',
    reasonCodes: string[],
  ): void {
    const event: SchemaValidationFailedEvent = {
      event_name: 'schema_validation_failed',
      page_type: pageType,
      schema_type: schemaType,
      reason_codes: reasonCodes,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitMetaValidationPassed(
    pageType: 'home' | 'product' | 'blog' | 'resource',
  ): void {
    const event: MetaValidationPassedEvent = {
      event_name: 'meta_validation_passed',
      page_type: pageType,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  function emitMetaValidationFailed(
    pageType: 'home' | 'product' | 'blog' | 'resource',
    missingFields: string[],
  ): void {
    const event: MetaValidationFailedEvent = {
      event_name: 'meta_validation_failed',
      page_type: pageType,
      missing_fields: missingFields,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit form submitted event (story 116-122)
   *
   * @param formId - Unique form identifier
   * @param formName - Human-readable form name
   * @param pagePath - Page path where the form is located
   */
  function emitFormSubmitted(formId: string, formName: string, pagePath: string): void {
    const event: FormSubmittedEvent = {
      event_name: 'form_submitted',
      form_id: formId,
      form_name: formName,
      page_path: pagePath,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit resource downloaded event (story 116-122)
   *
   * @param resourceName - Name of the resource
   * @param resourceUrl - URL of the resource
   * @param resourceType - File type (e.g., 'pdf', 'csv')
   * @param pagePath - Page path where the download was initiated
   */
  function emitResourceDownloaded(
    resourceName: string,
    resourceUrl: string,
    resourceType: string,
    pagePath: string
  ): void {
    const event: ResourceDownloadedEvent = {
      event_name: 'resource_downloaded',
      resource_name: resourceName,
      resource_url: resourceUrl,
      resource_type: resourceType,
      page_path: pagePath,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit purchase completed event (story 116-122)
   *
   * @param product - Product name (e.g., 'review', 'verify', 'insight')
   * @param plan - Plan/tier selected
   * @param value - Monetary value of the purchase
   * @param currency - Currency code (e.g., 'USD')
   */
  function emitPurchaseCompleted(
    product: string,
    plan: string,
    value: number,
    currency: string
  ): void {
    const event: PurchaseCompletedEvent = {
      event_name: 'purchase_completed',
      product,
      plan,
      value,
      currency,
      timestamp: getCurrentTimestamp(),
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  /**
   * Emit booking completed event (story 113-112)
   * Called from the thank-you page after a confirmed Microsoft Bookings submission
   *
   * @param bookingDetails - Optional booking detail string from URL param; null or undefined if absent
   */
  function emitBookingCompleted(bookingDetails?: string | null): void {
    const event: BookingCompletedEvent = {
      event_name: 'booking_completed',
      source: 'microsoft_bookings',
      page_path: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: getCurrentTimestamp(),
      booking_details: bookingDetails ?? null,
    }
    safeEmitAnalytics(event, analyticsClient)
  }

  return {
    emitROIBlockRendered,
    emitSubstantiationLinkClicked,
    emitPricingBlockRendered,
    emitPricingCTAClicked,
    emitCTAClicked,
    emitPageView,
    emitBookingsEmbedStatus,
    emitBookingsFallbackTriggered,
    emitBlogSearchSubmitted,
    emitBlogSearchNoResults,
    emitBlogSearchResultClicked,
    emitBlogListingViewed,
    emitBlogArticleViewed,
    emitBlogArticleClicked,
    emitBlogPaginationClicked,
    emitBlogScrollDepth,
    emitBlogReadTime,
    emitBlogFilterApplied,
    emitBlogFilterCleared,
    emitSchemaValidationPassed,
    emitSchemaValidationFailed,
    emitMetaValidationPassed,
    emitMetaValidationFailed,
    emitFormSubmitted,
    emitResourceDownloaded,
    emitPurchaseCompleted,
    emitBookingCompleted,
  }
}

/**
 * Event interface for pricing block rendered
 */
interface PricingBlockRenderedPayload {
  event_name: 'pricing_block_rendered'
  product: string
  takeaways_count: number
  has_cta: boolean
  timestamp: string
}

/**
 * Event interface for pricing CTA clicked
 */
interface PricingCTAClickedPayload {
  event_name: 'pricing_cta_clicked'
  cta_text: string
  cta_url: string
  link_type: 'internal' | 'external'
  product: string
  timestamp: string
}

/**
 * Emit pricing block rendered event
 * Must be exported for composable testing
 */
export function createPricingBlockRenderedEvent(
  product: string,
  takeawaysCount: number,
  hasCTA: boolean
): PricingBlockRenderedPayload {
  return {
    event_name: 'pricing_block_rendered',
    product,
    takeaways_count: takeawaysCount,
    has_cta: hasCTA,
    timestamp: getCurrentTimestamp(),
  }
}

/**
 * Emit pricing CTA clicked event
 * Must be exported for composable testing
 */
export function createPricingCTAClickedEvent(
  ctaText: string,
  ctaUrl: string,
  linkType: 'internal' | 'external',
  product: string
): PricingCTAClickedPayload {
  return {
    event_name: 'pricing_cta_clicked',
    cta_text: ctaText,
    cta_url: ctaUrl,
    link_type: linkType,
    product,
    timestamp: getCurrentTimestamp(),
  }
}
